import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { supportApi } from '../api/support';
import { toast } from 'react-hot-toast';

const SupportTicketDetail = ({ ticket, onClose, onUpdate }) => {
  const { user, tokens } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (ticket) {
      fetchMessages();
    }
  }, [ticket]);

  // Listen for WebSocket notifications to refresh messages
  useEffect(() => {
    if (!ticket) return;

    const handleSupportMessage = (event) => {
      if (event.detail.ticketId === ticket.id) {
        fetchMessages();
      }
    };

    window.addEventListener('supportMessageReceived', handleSupportMessage);
    return () => {
      window.removeEventListener('supportMessageReceived', handleSupportMessage);
    };
  }, [ticket?.id]);

  const fetchMessages = async () => {
    if (!ticket) return;
    
    setLoading(true);
    try {
      const data = await supportApi.getTicketMessages(ticket.id, tokens?.access?.token);
      if (data.success) {
        setMessages(data.data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const messageData = {
        content: newMessage.trim()
      };
      const result = await supportApi.addTicketMessage(ticket.id, messageData, tokens?.access?.token);
      if (result.success) {
        setNewMessage('');
        await fetchMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error sending message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (updatingStatus) return;
    
    setUpdatingStatus(true);
    try {
      const result = await supportApi.updateTicket(ticket.id, { status: newStatus }, tokens?.access?.token);
      if (result.success) {
        onUpdate?.(result.data); // Notify parent component to refresh
        toast.success(`Ticket status updated to ${newStatus.replace('_', ' ')}`);
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.error('Error updating ticket status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'pending_user': 'bg-orange-100 text-orange-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'normal': 'bg-blue-100 text-blue-800',
      'high': 'bg-yellow-100 text-yellow-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageTypeIcon = (messageType) => {
    switch (messageType) {
      case 'user': return '👤';
      case 'agent': return '🎧';
      case 'system': return '🤖';
      default: return '💬';
    }
  };

  if (!ticket) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a ticket to view details
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold">{ticket.subject}</h2>
            <p className="text-gray-600 font-mono text-sm">{ticket.ticketNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Status:</span>
            <div className="flex items-center gap-2">
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                {ticket.status.replace('_', ' ')}
              </span>
              {user?.roleId === 1 && (
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="pending_user">Pending User</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              )}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Priority:</span>
            <div>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority}
              </span>
            </div>
          </div>
          <div>
            <span className="text-gray-600">Category:</span>
            <div>{ticket.category?.name}</div>
          </div>
          <div>
            <span className="text-gray-600">Created:</span>
            <div>{formatDate(ticket.createdDateTime)}</div>
          </div>
        </div>

        {ticket.dueDate && (
          <div className="mt-3">
            <span className="text-gray-600">Due Date: </span>
            <span className={new Date(ticket.dueDate) < new Date() ? 'text-red-600 font-medium' : 'text-gray-800'}>
              {formatDate(ticket.dueDate)}
              {new Date(ticket.dueDate) < new Date() && ' (Overdue)'}
            </span>
          </div>
        )}

        {/* Payment metadata for payment issues */}
        {ticket.metadata && Object.keys(ticket.metadata).length > 0 && (
          <div className="mt-4 bg-gray-50 rounded-md p-3">
            <h4 className="font-medium text-gray-900 mb-2">Additional Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {ticket.metadata.transactionId && (
                <div>
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="ml-2 font-mono">{ticket.metadata.transactionId}</span>
                </div>
              )}
              {ticket.metadata.amount && (
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <span className="ml-2">₱{ticket.metadata.amount}</span>
                </div>
              )}
              {ticket.metadata.paymentMethod && (
                <div>
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="ml-2">{ticket.metadata.paymentMethod}</span>
                </div>
              )}
              {ticket.metadata.paymentDate && (
                <div>
                  <span className="text-gray-600">Payment Date:</span>
                  <span className="ml-2">{formatDate(ticket.metadata.paymentDate)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Status Actions for Admins */}
        {user?.roleId === 1 && ticket.status !== 'closed' && (
          <div className="mt-4 flex gap-2 flex-wrap">
            {ticket.status !== 'in_progress' && (
              <button
                onClick={() => handleStatusChange('in_progress')}
                disabled={updatingStatus}
                className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded text-sm disabled:opacity-50"
              >
                Mark In Progress
              </button>
            )}
            {ticket.status !== 'pending_user' && (
              <button
                onClick={() => handleStatusChange('pending_user')}
                disabled={updatingStatus}
                className="px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded text-sm disabled:opacity-50"
              >
                Pending User Response
              </button>
            )}
            {ticket.status !== 'resolved' && (
              <button
                onClick={() => handleStatusChange('resolved')}
                disabled={updatingStatus}
                className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 rounded text-sm disabled:opacity-50"
              >
                Mark Resolved
              </button>
            )}
            <button
              onClick={() => handleStatusChange('closed')}
              disabled={updatingStatus}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm disabled:opacity-50"
            >
              Close Ticket
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="p-6 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="text-center py-4">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No messages yet</div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.messageType === 'user' && message.userId === user.id ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.messageType === 'user' && message.userId === user.id
                    ? 'bg-blue-600 text-white'
                    : message.messageType === 'agent'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span>{getMessageTypeIcon(message.messageType)}</span>
                    <span className="text-xs opacity-75">
                      {message.user?.name || 'System'} • {formatDate(message.createdDateTime)}
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap">{message.content}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send Message */}
      <div className="p-6 border-t">
        <form onSubmit={handleSendMessage} className="flex gap-3">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            rows={3}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportTicketDetail;